import React, { useState } from 'react';
import { useDatabase } from '../../context/DatabaseContext';
import { writeBatch, doc, collection } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { useT } from '../../utils/useTranslation';
import Card from '../../components/Card';
import { motion } from 'framer-motion';
import { DISTRICTS, LIT_FIELDS, BRANCHES, CENTRAL_BODY, DIST_COORDINATORS, BRANCH_OFFICERS, BRANCH_MEMBERS, SAMPLE_BOOKS, SAMPLE_EVENTS, SAMPLE_ACTIVITY_LOGS } from './seedConstants';

export default function SeedData() {
  const t = useT();
  const { db } = useDatabase();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const isRTL = t.language === 'sindhi';
  const now = new Date().toISOString();

  const seed = async () => {
    const b = writeBatch(db);

    // Districts
    DISTRICTS.forEach(d => b.set(doc(db,"districts",d.id), { name: d.name, nameSindhi: d.sd, createdAt: now }));

    // Literary Fields
    LIT_FIELDS.forEach(f => b.set(doc(db,"literary-fields",f.id), { name: f.name, nameSindhi: f.sd, createdAt: now }));

    // Branches
    BRANCHES.forEach(br => b.set(doc(db,"branches",br.id), {
      name: br.name, nameSindhi: br.sd, districtId: br.dist, cityVillage: br.city, cityVillageSindhi: br.citySD,
      description: br.desc, descriptionSindhi: br.descSD, status: 'active', createdAt: now
    }));

    // Central Body users
    CENTRAL_BODY.forEach(u => b.set(doc(db,"users",u.id), {
      displayName: u.name, displayNameSindhi: u.sd, email: u.email, role: u.role, login_disabled: false, createdAt: now
    }));

    // District Coordinators
    DIST_COORDINATORS.forEach(dc => {
      b.set(doc(db,"users",dc.id), {
        displayName: dc.name, displayNameSindhi: dc.sd, email: dc.email, role: "district_coordinator",
        districtId: dc.dist, login_disabled: false, createdAt: now
      });
      b.update(doc(db,"districts",dc.dist), { coordinatorId: dc.id });
    });

    // Branch Officers (members + users)
    BRANCH_OFFICERS.forEach(o => {
      b.set(doc(db,"members",o.mId), {
        full_name: o.name, full_name_sindhi: o.sd, literary_name: o.lit || '', literary_name_sindhi: '',
        contact_info: { email: `${o.name.split(' ')[0].toLowerCase()}@sas.pk`, phone: o.phone },
        status: "approved", membership_status: "Permanent", current_branch_id: o.brId,
        districtId: o.dist, userId: o.uId, literary_fields: [], education: [], createdAt: now
      });
      b.set(doc(db,"users",o.uId), {
        displayName: o.name, displayNameSindhi: o.sd, email: `${o.name.split(' ')[0].toLowerCase()}@sas.pk`,
        role: o.role, branchId: o.brId, districtId: o.dist, memberId: o.mId, login_disabled: false, createdAt: now
      });
      if (o.role === 'branch_secretary_general') {
        b.update(doc(db,"branches",o.brId), { secretaryId: o.mId });
      }
    });

    // Regular Members
    BRANCH_MEMBERS.forEach(m => {
      b.set(doc(db,"members",m.mId), {
        full_name: m.name, full_name_sindhi: m.sd, literary_name: '', literary_name_sindhi: '',
        contact_info: { email: `${m.name.split(' ')[0].toLowerCase()}@sas.pk`, phone: m.phone },
        status: "approved", membership_status: "Temporary", current_branch_id: m.brId,
        districtId: m.dist, userId: m.uId, literary_fields: [], education: [], createdAt: now
      });
      b.set(doc(db,"users",m.uId), {
        displayName: m.name, displayNameSindhi: m.sd, email: `${m.name.split(' ')[0].toLowerCase()}@sas.pk`,
        role: "member", branchId: m.brId, districtId: m.dist, memberId: m.mId, login_disabled: false, createdAt: now
      });
    });

    // Books
    SAMPLE_BOOKS.forEach(bk => b.set(doc(collection(db,"published_books")), { ...bk, createdAt: now }));

    // Events
    SAMPLE_EVENTS.forEach(ev => b.set(doc(collection(db,"events")), { ...ev, createdAt: now }));

    // Activity Logs
    SAMPLE_ACTIVITY_LOGS.forEach(log => b.set(doc(collection(db,"activity_logs")), { ...log, createdAt: now }));

    await b.commit();
  };

  const handleSeed = async () => {
    if (!window.confirm(isRTL ? "ڇا ڊيٽابيس ۾ ٽيسٽ ڊيٽا شامل ڪرڻ چاهيو ٿا؟" : "Seed the database with test data?")) return;
    setLoading(true);
    try { await seed(); toast.success(isRTL ? "ڊيٽابيس ڀري وئي!" : "Database seeded!"); setDone(true); }
    catch (e) { console.error(e); toast.error(e.message); }
    finally { setLoading(false); }
  };

  const items = [
    ['🗺️', isRTL?'ضلعا':'Districts', DISTRICTS.length],
    ['✍️', isRTL?'ادبي صنفون':'Literary Fields', LIT_FIELDS.length],
    ['🌿', isRTL?'شاخون':'Branches', BRANCHES.length],
    ['🏛️', isRTL?'مرڪزي باڊي':'Central Body', CENTRAL_BODY.length],
    ['📍', isRTL?'ضلعي ڪوآرڊينيٽر':'Dist. Coordinators', DIST_COORDINATORS.length],
    ['👔', isRTL?'شاخ آفيسرز':'Branch Officers', BRANCH_OFFICERS.length],
    ['👥', isRTL?'ريگولر ميمبر':'Regular Members', BRANCH_MEMBERS.length],
    ['📚', isRTL?'ڪتاب':'Books', SAMPLE_BOOKS.length],
    ['📅', isRTL?'تقريبون':'Events', SAMPLE_EVENTS.length],
  ];

  return (
    <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} style={{direction:isRTL?'rtl':'ltr',textAlign:isRTL?'right':'left'}}>
      <h1 style={{fontFamily:isRTL?'var(--font-sindhi)':'var(--font-display)',fontSize:isRTL?42:32,fontWeight:800,color:'var(--sindhi-navy)',marginBottom:4}}>
        {isRTL?'ڊيٽابيس سيڊ ٽول':'Database Seeding Tool'}
      </h1>
      <p style={{fontSize:14,color:'var(--sindhi-text-muted)',marginBottom:32,fontFamily:isRTL?'var(--font-sindhi)':'inherit'}}>
        {isRTL?'ٽيسٽ ڊيٽا سان ڊيٽابيس ڀريو — سڀ ڊيٽا ٻن ٻولين ۾':'Populate database with bilingual test data'}
      </p>

      <Card style={{padding:40,background:'white',border:'1px solid var(--sindhi-cream)',marginBottom:32}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:16}}>
          {items.map(([icon,label,count]) => (
            <div key={label} style={{display:'flex',alignItems:'center',gap:16,padding:'16px 20px',background:'var(--sindhi-ivory)',borderRadius:16,border:'1px solid var(--sindhi-cream)'}}>
              <span style={{fontSize:24}}>{icon}</span>
              <div>
                <div style={{fontSize:24,fontWeight:800,color:'var(--sindhi-navy)',fontFamily:'var(--font-display)'}}>{count}</div>
                <div style={{fontSize:12,fontWeight:700,color:'var(--sindhi-text-muted)',fontFamily:isRTL?'var(--font-sindhi)':'inherit'}}>{label}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card style={{padding:32,background:done?'#e1f9eb':'var(--sindhi-ivory)',border:`1px solid ${done?'#1da352':'var(--sindhi-cream)'}`}}>
        <div style={{display:'flex',alignItems:'center',gap:16,flexDirection:isRTL?'row-reverse':'row'}}>
          <div style={{flex:1}}>
            <p style={{fontSize:14,color:'var(--sindhi-text)',margin:0,fontFamily:isRTL?'var(--font-sindhi)':'inherit'}}>
              {done ? (isRTL?'✅ ڊيٽابيس ڀري وئي آهي.':'✅ Database seeded successfully.') : (isRTL?'⚠️ موجوده ڊيٽا ختم نه ٿيندي. صرف نئون شامل ٿيندو.':'⚠️ Existing data will not be deleted. Only new documents added.')}
            </p>
          </div>
          {!done && <motion.button whileHover={{scale:1.05}} whileTap={{scale:0.95}} onClick={handleSeed} disabled={loading}
            className="btn btn-primary" style={{height:56,padding:'0 36px',borderRadius:16,fontSize:16,fontWeight:800,background:'var(--sindhi-maroon)',border:'none',color:'white',whiteSpace:'nowrap',fontFamily:isRTL?'var(--font-sindhi)':'inherit',opacity:loading?0.6:1}}>
            {loading?(isRTL?'⏳ جاري...':'⏳ Seeding...'):(isRTL?'🌱 ڊيٽابيس ڀريو':'🌱 Seed Database')}
          </motion.button>}
        </div>
      </Card>
    </motion.div>
  );
}
